import { Callout, Tooltip } from "@octopusdeploy/design-system-components";
import type { ActionTemplateCategoryResource, ActionTemplateSearchResource } from "@octopusdeploy/octopus-server-client";
import { ActionHandlerCategory, Permission, ProcessType } from "@octopusdeploy/octopus-server-client";
import type { LinkHref } from "@octopusdeploy/portal-routes";
import { exhaustiveCheck } from "@octopusdeploy/type-utils";
import * as React from "react";
import type { StepEvent } from "~/analytics/Analytics";
import { Action, useAnalyticStepDispatch } from "~/analytics/Analytics";
import { renderCategoryTemplates, renderFilteredTemplates } from "~/areas/projects/components/Process/ActionTemplateSelector/ActionTemplateHelpers";
import { StepCard } from "~/areas/projects/components/Process/ActionTemplateSelector/StepCard";
import { hidePrereleaseActionTemplates } from "~/areas/projects/components/Process/ActionTemplateSelector/hidePrereleaseActionTemplates";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import type { ProcessIdentifier } from "~/areas/projects/components/Process/types";
import { isProcessOrProjectTemplateProcessIdentifier, isDeploymentOrRunbookProcessIdentifier } from "~/areas/projects/components/Process/types";
import { repository } from "~/clientInstance";
import filterActionTemplates from "~/components/ActionTemplates/filterActionTemplates";
import ExternalLink from "~/components/Navigation/ExternalLink/ExternalLink";
import { isAllowed } from "~/components/PermissionCheck/PermissionCheck";
import Section from "~/components/Section";
import styles from "./InstalledActionTemplateList.module.less";
interface InstalledStepTemplateListProps {
    templates: ActionTemplateSearchResource[];
    categories: ActionTemplateCategoryResource[];
    filter?: string;
    activeCategory?: ActionHandlerCategory;
    onPostSelectionUrlRequested: (template: ActionTemplateSearchResource) => LinkHref;
    onDetailsUrlRequested: (template: ActionTemplateSearchResource) => {
        href: LinkHref;
        external: boolean;
    };
    onCategorySelected?: (category: ActionHandlerCategory) => void;
}
export function InstalledActionTemplateList({ templates, activeCategory, filter, onPostSelectionUrlRequested, onDetailsUrlRequested }: InstalledStepTemplateListProps) {
    const processContext = useProcessContext();
    const processIdentifier = processContext.state.processIdentifier;
    const projectId = isDeploymentOrRunbookProcessIdentifier(processIdentifier) ? processIdentifier.projectId : undefined;
    const dispatchStep = useAnalyticStepDispatch(projectId);
    const [spaceMap, setSpaceMap] = React.useState<Map<string, string>>(new Map());
    React.useEffect(() => {
        if (!isProcessOrProjectTemplateProcessIdentifier(processIdentifier)) {
            return;
        }
        const fetchSpaces = async () => {
            try {
                const spaceIds = new Set<string>();
                templates.forEach((template) => {
                    if (template.SpaceId && !template.IsBuiltIn && !template.CommunityActionTemplateId) {
                        spaceIds.add(template.SpaceId);
                    }
                });
                if (spaceIds.size > 0) {
                    const spaces = await repository.forSystem().Spaces.all({ ids: Array.from(spaceIds) });
                    const map = new Map<string, string>();
                    spaces.forEach((space) => {
                        map.set(space.Id, space.Name);
                    });
                    setSpaceMap(map);
                }
            }
            catch (error) {
                console.error("Failed to fetch space information:", error);
            }
        };
        fetchSpaces();
    }, [templates, processIdentifier]);
    const categoryTemplates = activeCategory ? templates.filter((x) => x.Categories.includes(activeCategory)) : [];
    const isFiltering = !!filter;
    const filteredResults = filterActionTemplates(templates, filter ?? "").filter(hidePrereleaseActionTemplates);
    const getPrimaryAction = (template: ActionTemplateSearchResource) => {
        const isAStepTemplateCardInProcessTemplate = isProcessOrProjectTemplateProcessIdentifier(processIdentifier) && !template.IsBuiltIn && !template.CommunityActionTemplateId;
        const dispatchStepAction = () => {
            const ev: StepEvent = {
                action: Action.Add,
                stepCategory: template.Categories[1] ?? template.Category,
                stepTemplate: template.Name,
                resource: getResourceName(processIdentifier),
            };
            dispatchStep("Add step template", ev);
        };
        return {
            label: isAStepTemplateCardInProcessTemplate ? "Import" : "Add Step",
            href: onPostSelectionUrlRequested(template),
            onClick: dispatchStepAction,
        };
    };
    const renderStepCard = (template: ActionTemplateSearchResource, disabled: boolean) => {
        const chipDetails = getTemplateChipDetails(template, filter, activeCategory);
        const subHeading = template.IsBuiltIn
            ? "By Octopus Deploy"
            : isProcessOrProjectTemplateProcessIdentifier(processIdentifier) && template.SpaceId && spaceMap.has(template.SpaceId)
                ? `Installed · ${spaceMap.get(template.SpaceId) ?? "Installed"}`
                : "Installed";
        return (<StepCard key={template.Type + template.Id} heading={template.Name} subHeading={subHeading} logo={template.Links.Logo} chipLabel={chipDetails.label} chipType={chipDetails.type} description={template.Description} dotpoints={template.Features} truncateDotpoints={true} primaryAction={getPrimaryAction(template)} secondaryAction={getSecondaryAction(template, onDetailsUrlRequested)} disabled={disabled}/>);
    };
    const renderSingle = (template: ActionTemplateSearchResource) => {
        const stepSupportedResult = isStepSupported(template, processIdentifier);
        return stepSupportedResult === true ? renderStepCard(template, false) : <Tooltip content={stepSupportedResult}>{renderStepCard(template, true)}</Tooltip>;
    };
    return (<div className={styles.container}>
            <>
                {!isAllowed({ permission: Permission.ActionTemplateView }) && (<Section className={styles.sectionDivider}>
                        <Callout type={"information"} title={"Permission required"}>
                            You can't see custom installed step templates because you don't have <ExternalLink href="UserRoles">{Permission.ActionTemplateView}</ExternalLink> permission.
                        </Callout>
                    </Section>)}
                {renderCategoryTemplates({
            categoryTemplates,
            activeCategory,
            sectionHeader: `Installed Step Templates (${categoryTemplates.length})`,
            renderSingleTemplate: renderSingle,
        })}
            </>
            {isFiltering &&
            renderFilteredTemplates({
                filteredResults,
                sectionHeader: `Installed Step Templates (${filteredResults.length})`,
                renderSingleTemplate: renderSingle,
                transitionKey: "filteredSteps",
            })}
        </div>);
}
function getResourceName(processIdentifier: ProcessIdentifier): string {
    const processType = processIdentifier.type;
    switch (processType) {
        case ProcessType.Deployment:
            return "Deployment Process";
        case ProcessType.Runbook:
            return "Runbook";
        case ProcessType.ProcessTemplate:
            return "Process Template";
        case ProcessType.TemplatedProject:
            return "Templated project";
        case ProcessType.ProjectTemplate:
            return "Project Template";
        default:
            exhaustiveCheck(processType, "Unknown process type");
    }
}
function isStepSupported(template: ActionTemplateSearchResource, processIdentifier: ProcessIdentifier): StepSupportedResult {
    if (isProcessOrProjectTemplateProcessIdentifier(processIdentifier)) {
        const stepPackageFrameworkStepTypes = ["deploy-a-bicep-template", "aws-s3-create-bucket", "aws-ecs", "aws-ecs-update-service"];
        if (stepPackageFrameworkStepTypes.some((type) => type === template.Type.toLowerCase())) {
            return "Step package framework steps are not supported";
        }
    }
    return true;
}
function isRecommendedStep(step: ActionTemplateSearchResource): boolean {
    const recommendedActionTypes = ["Octopus.KubernetesDeployRawYaml", "kustomize", "Octopus.Kubernetes.Kustomize", "Octopus.HelmChartUpgrade"];
    return step.IsBuiltIn && recommendedActionTypes.some((x) => x.toLowerCase() === step.Type.toLowerCase());
}
function isEarlyAccess(step: ActionTemplateSearchResource): boolean {
    const earlyAccessActionTypes = ["Octopus.ArgoCDUpdateImageTags", "Octopus.ArgoCDUpdateManifests"];
    return step.IsBuiltIn && earlyAccessActionTypes.some((x) => x.toLowerCase() === step.Type.toLowerCase());
}
function getTemplateChipDetails(step: ActionTemplateSearchResource, filter?: string, activeCategory?: ActionHandlerCategory): {
    label: string;
    type: "info" | "primary" | "success" | undefined;
} {
    if (isEarlyAccess(step)) {
        return { label: "Early Access", type: "info" };
    }
    if (step.IsBuiltIn && !!(filter || activeCategory === ActionHandlerCategory.Featured)) {
        return { label: "", type: undefined };
    }
    if (isRecommendedStep(step)) {
        return { label: "Recommended", type: "success" };
    }
    if (step.CommunityActionTemplateId) {
        return { label: "Community Contributed", type: "primary" };
    }
    if (!step.CommunityActionTemplateId && step.Categories.includes(ActionHandlerCategory.StepTemplate)) {
        return { label: "Custom Template", type: "info" };
    }
    return { label: "", type: undefined };
}
function getSecondaryAction(template: ActionTemplateSearchResource, onDetailsUrlRequested: (template: ActionTemplateSearchResource) => {
    href: LinkHref;
    external: boolean;
}): {
    label: string;
    href: LinkHref;
    external: boolean;
} | undefined {
    const urlDetails = onDetailsUrlRequested(template);
    if (template.HasUpdate) {
        return { label: "Update Available", ...urlDetails };
    }
    if (template.CommunityActionTemplateId && template.Categories.includes(ActionHandlerCategory.StepTemplate)) {
        return { label: "More Info", ...urlDetails };
    }
    return undefined;
}
type StepSupportedResult = true | UnsupportedStepReason;
type UnsupportedStepReason = "Step package framework steps are not supported";
